import { useEffect, useMemo, useRef, useState } from "react";
import { api, currency, fmtDate } from "@/lib/api";
import { downloadInvoicePdf, generatePdfBase64, makeDocFilename } from "@/lib/pdf";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import InvoiceDocument from "@/components/InvoiceDocument";
import InvoiceEditDialog from "@/components/InvoiceEditDialog";
import PackingSlip from "@/components/PackingSlip";
import ConfirmDialog from "@/components/ConfirmDialog";
import EmailPreviewModal from "@/components/EmailPreviewModal";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Eye, Trash2, Download, CheckCircle2, Clock, Search, Pencil,
  Truck, AlertCircle, Mail, PackageCheck, Package, FileText,
} from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  { key: "action", label: "Needs Action" },
  { key: "all", label: "All" },
  { key: "outstanding", label: "Outstanding" },
  { key: "pending", label: "Pending Delivery" },
  { key: "paid", label: "Paid" },
  { key: "delivered", label: "Delivered" },
];

export default function Orders() {
  const [invoices, setInvoices] = useState([]);
  const [filter, setFilter] = useState("action");
  const [query, setQuery] = useState("");

  // Invoice view
  const [selected, setSelected] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const docRef = useRef(null);

  // Delivery note view
  const [packing, setPacking] = useState(null);
  const [packingOpen, setPackingOpen] = useState(false);
  const [packingNotes, setPackingNotes] = useState("");
  const [packingDownloading, setPackingDownloading] = useState(false);
  const packRef = useRef(null);

  const [deleteTarget, setDeleteTarget] = useState(null); // { id, number }
  const [deleting, setDeleting] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  // Send invoice email
  const [sendInv, setSendInv] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendEmail, setSendEmail] = useState("");
  const [sendDocType, setSendDocType] = useState("invoice");
  const [sending, setSending] = useState(false);

  // Edit
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/invoices");
    setInvoices(data);
  };
  useEffect(() => { load(); }, []);

  const isOverdue = (inv) => inv && !inv.paid && inv.due_date && new Date(inv.due_date) < new Date();

  const filtered = useMemo(() => {
    let list = invoices;
    switch (filter) {
      case "action":    list = invoices.filter(i => !i.paid || !i.delivered); break;
      case "outstanding": list = invoices.filter(i => !i.paid); break;
      case "pending":   list = invoices.filter(i => !i.delivered); break;
      case "paid":      list = invoices.filter(i => i.paid); break;
      case "delivered": list = invoices.filter(i => i.delivered); break;
      default:          break;
    }
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (inv) =>
        (inv.invoice_number || "").toLowerCase().includes(q) ||
        (inv.customer_name || "").toLowerCase().includes(q) ||
        (inv.order_number || "").toLowerCase().includes(q)
    );
  }, [invoices, filter, query]);

  const markPaid = async (id, paid, e) => {
    if (e) e.stopPropagation();
    try {
      const { data } = await api.patch(`/invoices/${id}/paid`, { paid });
      toast.success(paid ? "Marked as paid" : "Marked as outstanding");
      setSelected((s) => (s && s.id === id ? data : s));
      load();
    } catch { toast.error("Failed to update invoice"); }
  };

  const markDelivered = async (id, delivered, e) => {
    if (e) e.stopPropagation();
    try {
      const { data } = await api.patch(`/invoices/${id}/delivered`, { delivered });
      toast.success(delivered ? "Marked as delivered" : "Marked as pending delivery");
      setSelected((s) => (s && s.id === id ? data : s));
      load();
    } catch { toast.error("Failed to update delivery status"); }
  };

  const remove = (id, invoiceNumber, e) => {
    if (e) e.stopPropagation();
    setDeleteTarget({ id, number: invoiceNumber });
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/invoices/${deleteTarget.id}`);
      toast.success("Order deleted");
      setDeleteTarget(null);
      load();
    } catch { toast.error("Failed to delete order"); }
    finally { setDeleting(false); }
  };

  const openEdit = (inv, e) => { if (e) e.stopPropagation(); setEditing(inv); setEditOpen(true); };
  const openPacking = (inv, e) => { if (e) e.stopPropagation(); setPacking(inv); setPackingNotes(""); setPackingOpen(true); };

  const openSend = (inv, docType, e) => {
    if (e) e.stopPropagation();
    setSendInv(inv);
    setSendEmail(inv.customer_email || "");
    setSendDocType(docType);
    setSendOpen(true);
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const filename = makeDocFilename(selected?.customer_tag, "Invoice", selected?.invoice_number);
      await downloadInvoicePdf(docRef.current, filename);
      toast.success("PDF downloaded");
    } catch { toast.error("Could not generate PDF"); }
    finally { setDownloading(false); }
  };

  const downloadPacking = async () => {
    setPackingDownloading(true);
    try {
      const filename = makeDocFilename(packing?.customer_tag, "DeliveryNote", packing?.invoice_number);
      await downloadInvoicePdf(packRef.current, filename);
      toast.success("Delivery note downloaded");
    } catch { toast.error("Could not generate delivery note"); }
    finally { setPackingDownloading(false); }
  };

  const handleSend = async ({ toEmail, subject, customBodyHtml }) => {
    setSending(true);
    try {
      let pdf_base64 = null;
      let filename;
      if (sendDocType === "invoice") {
        filename = makeDocFilename(sendInv?.customer_tag, "Invoice", sendInv?.invoice_number);
        setSelected(sendInv);
        await new Promise((r) => setTimeout(r, 120));
        if (docRef.current) pdf_base64 = await generatePdfBase64(docRef.current);
      } else {
        filename = makeDocFilename(sendInv?.customer_tag, "DeliveryNote", sendInv?.invoice_number);
        setPacking(sendInv);
        setPackingOpen(false);
        await new Promise((r) => setTimeout(r, 120));
        if (packRef.current) pdf_base64 = await generatePdfBase64(packRef.current);
      }
      await api.post(`/invoices/${sendInv.id}/send`, {
        to_email: toEmail,
        pdf_base64,
        filename,
        doc_type: sendDocType,
        custom_subject: subject,
        custom_body_html: customBodyHtml,
      });
      toast.success(`${sendDocType === "invoice" ? "Invoice" : "Delivery note"} sent to ${toEmail}`);
      setSendOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="orders-page">
      <h1 className="font-heading text-3xl font-bold text-[#032352]">Orders</h1>
      <p className="text-[#5B5C5F] mt-1 mb-5">All orders — manage invoices, delivery notes, and status.</p>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="orders-filter-tabs">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
              filter === f.key
                ? "bg-[#032352] text-white border-[#032352]"
                : "bg-white text-[#5B5C5F] border-slate-200 hover:border-[#032352] hover:text-[#032352]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="relative w-full max-w-sm mb-4">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5B5C5F]" />
        <Input
          data-testid="orders-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by invoice #, customer or order #..."
          className="pl-9"
        />
      </div>

      <div className="bg-white rounded-md border border-slate-200 overflow-x-auto">
        <Table className="min-w-[860px]">
          <TableHeader>
            <TableRow className="bg-[#F8FAFC]">
              <TableHead>Invoice #</TableHead>
              <TableHead>Order #</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Due</TableHead>
              <TableHead>Payment</TableHead>
              <TableHead>Delivery</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="w-52"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} className="text-center text-[#5B5C5F] py-10">
                  No orders found.
                </TableCell>
              </TableRow>
            )}
            {filtered.map((inv) => (
              <TableRow
                key={inv.id}
                data-testid={`order-row-${inv.id}`}
                className={`cursor-pointer ${isOverdue(inv) ? "bg-red-50 hover:bg-red-100" : ""}`}
                onClick={() => setSelected(inv)}
              >
                <TableCell className="font-mono-num font-medium text-[#032352]">{inv.invoice_number}</TableCell>
                <TableCell className="font-mono-num text-[#5B5C5F] text-sm">{inv.order_number || "—"}</TableCell>
                <TableCell className="text-[#032352]">{inv.customer_name}</TableCell>
                <TableCell className="text-[#5B5C5F] text-sm">{fmtDate(inv.invoice_date)}</TableCell>
                <TableCell className={`text-sm font-medium ${isOverdue(inv) ? "text-red-600" : "text-[#5B5C5F]"}`}>
                  {fmtDate(inv.due_date)}
                  {isOverdue(inv) && <AlertCircle className="w-3 h-3 inline ml-1 text-red-500" />}
                </TableCell>
                <TableCell>
                  {inv.paid ? (
                    <Badge className="bg-emerald-500 hover:bg-emerald-500" data-testid={`pay-status-${inv.id}`}>Paid</Badge>
                  ) : isOverdue(inv) ? (
                    <Badge className="bg-red-500 hover:bg-red-500 text-white" data-testid={`pay-status-${inv.id}`}>Overdue</Badge>
                  ) : (
                    <Badge variant="outline" className="text-amber-600 border-amber-400" data-testid={`pay-status-${inv.id}`}>Outstanding</Badge>
                  )}
                </TableCell>
                <TableCell>
                  {inv.delivered ? (
                    <Badge className="bg-emerald-500 hover:bg-emerald-500" data-testid={`del-status-${inv.id}`}>Delivered</Badge>
                  ) : (
                    <Badge variant="outline" className="text-slate-500 border-slate-300" data-testid={`del-status-${inv.id}`}>Pending</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right font-mono-num text-[#032352]">{currency(inv.total)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2 justify-end flex-wrap">
                    {/* Payment */}
                    {!inv.paid ? (
                      <button data-testid={`mark-paid-${inv.id}`} onClick={(e) => markPaid(inv.id, true, e)} title="Mark as paid" className="text-slate-400 hover:text-emerald-600 transition-colors">
                        <CheckCircle2 className="w-4 h-4" />
                      </button>
                    ) : (
                      <button data-testid={`mark-unpaid-${inv.id}`} onClick={(e) => markPaid(inv.id, false, e)} title="Mark as outstanding" className="text-emerald-600 hover:text-amber-600 transition-colors">
                        <Clock className="w-4 h-4" />
                      </button>
                    )}
                    {/* Delivery */}
                    {!inv.delivered ? (
                      <button data-testid={`mark-delivered-${inv.id}`} onClick={(e) => markDelivered(inv.id, true, e)} title="Mark as delivered" className="text-slate-400 hover:text-emerald-600 transition-colors">
                        <PackageCheck className="w-4 h-4" />
                      </button>
                    ) : (
                      <button data-testid={`mark-undelivered-${inv.id}`} onClick={(e) => markDelivered(inv.id, false, e)} title="Mark as pending delivery" className="text-emerald-600 hover:text-amber-600 transition-colors">
                        <Package className="w-4 h-4" />
                      </button>
                    )}
                    {/* Invoice actions */}
                    <button data-testid={`send-invoice-${inv.id}`} onClick={(e) => openSend(inv, "invoice", e)} title="Email invoice" className="text-slate-400 hover:text-[#032352] transition-colors">
                      <Mail className="w-4 h-4" />
                    </button>
                    <button data-testid={`view-invoice-${inv.id}`} onClick={(e) => { e.stopPropagation(); setSelected(inv); }} title="View invoice" className="text-slate-400 hover:text-[#032352] transition-colors">
                      <FileText className="w-4 h-4" />
                    </button>
                    {/* Delivery note */}
                    <button data-testid={`view-delivery-${inv.id}`} onClick={(e) => openPacking(inv, e)} title="View delivery note" className="text-slate-400 hover:text-[#032352] transition-colors">
                      <Truck className="w-4 h-4" />
                    </button>
                    {/* Edit / Delete */}
                    <button data-testid={`edit-order-${inv.id}`} onClick={(e) => openEdit(inv, e)} title="Edit order" className="text-slate-400 hover:text-[#032352] transition-colors">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button data-testid={`delete-order-${inv.id}`} onClick={(e) => remove(inv.id, inv.invoice_number, e)} title="Delete order" className="text-slate-400 hover:text-red-500 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Invoice view dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0" data-testid="invoice-view-dialog">
          <DialogTitle className="sr-only">Invoice {selected?.invoice_number}</DialogTitle>
          <DialogDescription className="sr-only">Invoice document for {selected?.customer_name}</DialogDescription>
          <div className="flex justify-between items-center gap-2 p-4 pb-0 flex-wrap">
            <div className="flex gap-2">
              {selected?.paid ? (
                <Badge className="bg-emerald-500 hover:bg-emerald-500">Paid</Badge>
              ) : isOverdue(selected) ? (
                <Badge className="bg-red-500 hover:bg-red-500 text-white">Overdue</Badge>
              ) : (
                <Badge variant="outline" className="text-amber-600 border-amber-400">Outstanding</Badge>
              )}
              {selected?.delivered ? (
                <Badge className="bg-emerald-500 hover:bg-emerald-500">Delivered</Badge>
              ) : (
                <Badge variant="outline" className="text-slate-500 border-slate-300">Pending Delivery</Badge>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              {selected && !selected.paid && (
                <Button size="sm" onClick={() => markPaid(selected.id, true)} data-testid="dialog-mark-paid-button" className="bg-emerald-500 hover:bg-emerald-600">
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Mark Paid
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={() => openSend(selected, "invoice")} data-testid="dialog-send-invoice-button" className="border-[#032352] text-[#032352]">
                <Mail className="w-4 h-4 mr-2" /> Email Invoice
              </Button>
              <Button variant="outline" size="sm" onClick={downloadPdf} disabled={downloading} data-testid="print-invoice-button" className="border-[#032352] text-[#032352]">
                <Download className="w-4 h-4 mr-2" /> {downloading ? "Generating…" : "Download PDF"}
              </Button>
            </div>
          </div>
          <div ref={docRef}>
            <InvoiceDocument invoice={selected} />
          </div>
        </DialogContent>
      </Dialog>

      {/* Delivery note dialog */}
      <Dialog open={packingOpen} onOpenChange={setPackingOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0" data-testid="packing-dialog">
          <DialogTitle className="sr-only">Delivery Note</DialogTitle>
          <DialogDescription className="sr-only">Delivery note</DialogDescription>
          <div className="p-4 pb-0 space-y-3">
            <div>
              <Label className="text-xs">Notes (added to the delivery note)</Label>
              <Textarea data-testid="packing-notes-input" value={packingNotes} onChange={(e) => setPackingNotes(e.target.value)} placeholder="e.g. Deliver to rear entrance, call on arrival..." />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => openSend(packing, "delivery_note")} data-testid="email-packing-button" className="border-[#032352] text-[#032352]">
                <Mail className="w-4 h-4 mr-2" /> Email Delivery Note
              </Button>
              <Button variant="outline" size="sm" onClick={downloadPacking} disabled={packingDownloading} data-testid="download-packing-button" className="border-[#032352] text-[#032352]">
                <Download className="w-4 h-4 mr-2" /> {packingDownloading ? "Generating…" : "Download Delivery Note"}
              </Button>
            </div>
          </div>
          <div ref={packRef}>
            <PackingSlip invoice={packing} notes={packingNotes} />
          </div>
        </DialogContent>
      </Dialog>

      <InvoiceEditDialog invoice={editing} open={editOpen} onOpenChange={setEditOpen} onSaved={load} />

      <ConfirmDialog
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        loading={deleting}
        title={`Delete Order ${deleteTarget?.number || ""}?`}
        description="This will permanently delete the order, its invoice, and its delivery note. This cannot be undone."
      />

      {/* Send email — live preview modal */}
      <EmailPreviewModal
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        title={`Send ${sendDocType === "invoice" ? "Invoice" : "Delivery Note"} — ${sendInv?.invoice_number}`}
        toEmail={sendEmail}
        customerEmail={sendInv?.customer_email}
        docType={sendDocType}
        invoice={sendInv}
        attachmentName={
          sendInv
            ? makeDocFilename(
                sendInv.customer_tag,
                sendDocType === "invoice" ? "Invoice" : "DeliveryNote",
                sendInv.invoice_number
              )
            : undefined
        }
        onSend={handleSend}
        sending={sending}
      />
    </div>
  );
}
